import React, { useEffect, useState } from "react";
import { confirm } from "../utils/confirm";
import { messaggioErrore } from "../utils/erroriUtente";
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useControlloDoppioni } from "./ControlloDoppioni";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { Plus, X, Package, ChevronDown, ChevronUp } from "lucide-react";
import { chiaveBozza, useBozzaModulo } from "../utils/bozzeLocali";
import AvvisoBozza from "./AvvisoBozza";

const UM_OPTIONS = ["pz", "mt", "ml", "kg", "lt", "rotoli", "scatole", "sacchi", "m²", "altro"];

// Stessi stati di MagazzinoPage.js, dove il magazziniere lavora davvero le richieste — qui,
// dentro la commessa, si vede solo lo stato generale: niente Evaso/Saldo, che restano
// visibili e compilabili solo a chi ha accesso alla sezione Magazzino/Acquisti.
const STATO_CONFIG = {
  in_attesa:     { label:"In attesa",       colore:"#d97706", bg:"#fff7ed", icon:"⏳" },
  in_lavorazione:{ label:"In lavorazione",  colore:"#2563eb", bg:"#eff6ff", icon:"🔄" },
  parziale:      { label:"Parz. evasa",     colore:"#7c3aed", bg:"#ede9fe", icon:"⚠️" },
  chiusa:        { label:"Chiusa",          colore:"#059669", bg:"#d1fae5", icon:"✅" },
  annullata:     { label:"Annullata",       colore:"#9ca3af", bg:"#f3f4f6", icon:"❌" },
};

// Definito fuori per evitare bug perdita focus
function RigaInput({ riga, index, onChange, onRemove, isOnly }) {
  return (
    <div style={{display:"grid", gridTemplateColumns:"2fr 120px 80px 90px 1fr auto", gap:8, alignItems:"center", marginBottom:8}}>
      <input className="input" placeholder="Descrizione materiale *" value={riga.descrizione}
        onChange={e=>onChange(index,"descrizione",e.target.value)}/>
      <input className="input" placeholder="Codice (opz.)" value={riga.codice}
        onChange={e=>onChange(index,"codice",e.target.value)}/>
      <input className="input" type="text" inputMode="decimal" placeholder="Qta" value={riga.quantita}
        onChange={e=>onChange(index,"quantita",e.target.value)}/>
      <select className="input" value={riga.unitaMisura} onChange={e=>onChange(index,"unitaMisura",e.target.value)}>
        {UM_OPTIONS.map(u=><option key={u} value={u}>{u}</option>)}
      </select>
      <input className="input" placeholder="Note (opz.)" value={riga.note}
        onChange={e=>onChange(index,"note",e.target.value)}/>
      <button type="button" onClick={()=>onRemove(index)} disabled={isOnly}
        style={{background: isOnly?"#f3f4f6":"#fee2e2",border:"none",borderRadius:6,width:32,height:32,cursor:isOnly?"not-allowed":"pointer",
          color:isOnly?"#d1d5db":"#dc2626",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <X size={13}/>
      </button>
    </div>
  );
}

function NuovaRichiestaModal({ projectId, projectName, orgId, currentUser, onClose }) {
  const { verifica, finestra: finestraDoppioni } = useControlloDoppioni();
  const { addToast } = useToast();
  const [righe, setRighe] = useState([{ descrizione:"", codice:"", quantita:"", unitaMisura:"pz", note:"" }]);
  const [urgenza, setUrgenza] = useState("normale");
  const [noteGenerali, setNoteGenerali] = useState("");
  const [saving, setSaving] = useState(false);
  // Bozza automatica sul dispositivo (utils/bozzeLocali.js)
  const bozza = useBozzaModulo({
    chiave: chiaveBozza(currentUser?.uid, "richiestaMateriali", projectId, null),
    valore: { righe, urgenza, noteGenerali },
    info: { titolo: `Richiesta materiali — ${projectName || ""}`, vai: { page: "project", projectId, initialTab: "materiali" } },
    applica: d => { if (Array.isArray(d.righe)) setRighe(d.righe); if (d.urgenza) setUrgenza(d.urgenza); if (d.noteGenerali !== undefined) setNoteGenerali(d.noteGenerali); },
  });

  const aggiungiRiga = () => setRighe(r=>[...r, { descrizione:"", codice:"", quantita:"", unitaMisura:"pz", note:"" }]);
  const rimuoviRiga = i => setRighe(r=>r.filter((_,idx)=>idx!==i));
  const updateRiga = (i, field, val) => setRighe(r=>r.map((riga,idx)=>idx===i?{...riga,[field]:val}:riga));

  const submit = async e => {
    e.preventDefault();
    const righeValide = righe.filter(r=>r.descrizione.trim()).map(r=>({...r, evaso:"0"}));
    if (righeValide.length===0) { addToast("Inserisci almeno un materiale","error"); return; }
    setSaving(true);
    // Lo stesso materiale è già stato chiesto o ordinato per questa commessa? (W408)
    const esito = await verifica({ orgId, projectId, righe: righeValide });
    if (!esito.procedi) { setSaving(false); return; }
    try {
      await addDoc(collection(db,"richiesteMateriali"), {
        ...esito.campi,
        projectId, projectName, organizationId: orgId,
        richiestaFrom: currentUser.uid,
        richiestaFromName: currentUser.displayName || "Utente",
        righe: righeValide,
        urgenza, noteGenerali,
        stato: "in_attesa",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      addToast("Richiesta inviata al magazzino ✅","success");
      onClose();
    } catch(err) { addToast(messaggioErrore(err), "error"); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      {finestraDoppioni}
      <div className="modal" style={{maxWidth:750, maxHeight:"90vh", overflowY:"auto"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:700}}>📦 Nuova richiesta materiali</h3>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        {bozza.trovata && <AvvisoBozza testo="Hai una richiesta materiali iniziata e non inviata"
          salvataIl={bozza.trovata.salvataIl} onRiprendi={bozza.riprendi} onElimina={bozza.scarta} />}
        <form onSubmit={e => bozza.salvaCon(() => submit(e))}>
          <div style={{display:"grid",gridTemplateColumns:"2fr 120px 80px 90px 1fr auto",gap:8,marginBottom:4}}>
            {["Descrizione *","Codice","Qta","U.M.","Note",""].map((h,i)=>(
              <div key={i} style={{fontSize:11,fontWeight:600,color:"#9ca3af"}}>{h}</div>
            ))}
          </div>
          {righe.map((riga,i)=>(
            <RigaInput key={i} riga={riga} index={i} onChange={updateRiga} onRemove={rimuoviRiga} isOnly={righe.length===1}/>
          ))}
          <button type="button" onClick={aggiungiRiga}
            style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"1px dashed #d1d5db",borderRadius:8,padding:"7px 14px",cursor:"pointer",color:"#7c3aed",fontSize:12,fontWeight:600,fontFamily:"'Inter',sans-serif",marginBottom:16}}>
            <Plus size={13}/> Aggiungi riga
          </button>

          <div style={{display:"grid",gridTemplateColumns:"1fr 2fr",gap:12,marginBottom:16}}>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Urgenza</label>
              <div style={{display:"flex",gap:8}}>
                {[{val:"normale",label:"Normale",col:"#059669"},{val:"urgente",label:"🚨 Urgente",col:"#dc2626"}].map(u=>(
                  <button key={u.val} type="button" onClick={()=>setUrgenza(u.val)}
                    style={{flex:1,padding:"7px 0",border:`2px solid ${urgenza===u.val?u.col:"#e5e7eb"}`,borderRadius:8,background:urgenza===u.val?u.col+"15":"#fff",color:urgenza===u.val?u.col:"#6b7280",fontWeight:urgenza===u.val?700:400,cursor:"pointer",fontFamily:"'Inter',sans-serif",fontSize:13}}>
                    {u.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Note generali per il magazzino</label>
              <input className="input" value={noteGenerali} onChange={e=>setNoteGenerali(e.target.value)} placeholder="Es. Serve entro venerdì mattina"/>
            </div>
          </div>

          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving?"Invio...":"Invia al magazzino"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function RichiestaMaterialiTab({ projectId, projectName, orgId, projectData, onNavigate }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { addToast } = useToast();
  const commessaChiusa = projectData?.statoCommessa === "chiusa";
  const [richieste, setRichieste] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [aperte, setAperte] = useState({});

  useEffect(()=>{
    if (!projectId) return;
    const q = query(collection(db,"richiesteMateriali"), where("projectId","==",projectId), where("organizationId","==",orgId));
    return onSnapshot(q, snap=>{
      const list = snap.docs.map(d=>({id:d.id,...d.data()}));
      list.sort((a,b)=>{
        if (a.urgenza==="urgente" && b.urgenza!=="urgente") return -1;
        if (b.urgenza==="urgente" && a.urgenza!=="urgente") return 1;
        return (b.createdAt?.toMillis()||0)-(a.createdAt?.toMillis()||0);
      });
      setRichieste(list);
    });
  },[projectId]);

  const toggleAperta = id => setAperte(a=>({...a,[id]:!a[id]}));

  const annulla = async id => {
    if (!await confirm("Annullare questa richiesta?", { type:"danger" })) return;
    await updateDoc(doc(db,"richiesteMateriali",id),{stato:"annullata",updatedAt:serverTimestamp()});
  };

  return (
    <div style={{padding:24}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
        <div>
          <h3 style={{fontSize:16,fontWeight:700}}>📦 Richieste al magazzino</h3>
          <div style={{fontSize:12,color:"#9ca3af",marginTop:2}}>{richieste.length} richieste per questa commessa</div>
        </div>
        <button onClick={()=>{ if(commessaChiusa){addToast("Commessa chiusa — non è più possibile richiedere materiali per questa commessa","error");return;} setShowModal(true); }} className="btn btn-primary" style={{display:"flex",alignItems:"center",gap:6}}>
          <Plus size={14}/> Nuova richiesta
        </button>
      </div>

      {richieste.length===0 ? (
        <div style={{textAlign:"center",padding:48,color:"#9ca3af"}}>
          <Package size={40} style={{marginBottom:12,opacity:0.3}}/>
          <div style={{fontSize:14,fontWeight:600}}>Nessuna richiesta materiali</div>
          <div style={{fontSize:13,marginTop:4}}>Crea una richiesta per il magazzino</div>
        </div>
      ) : richieste.map(r=>{
        const cfg = STATO_CONFIG[r.stato]||STATO_CONFIG.in_attesa;
        const isAperta = aperte[r.id];
        return (
          <div key={r.id} style={{background:"#fff",border:`1px solid ${r.urgenza==="urgente"?"#fca5a5":"#e5e7eb"}`,borderRadius:12,marginBottom:12,overflow:"hidden",borderLeft:`4px solid ${cfg.colore}`}}>
            <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",cursor:"pointer"}} onClick={()=>toggleAperta(r.id)}>
              <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:12,background:cfg.bg,color:cfg.colore}}>{cfg.icon} {cfg.label}</span>
              {r.urgenza==="urgente" && <span style={{fontSize:11,fontWeight:700,color:"#dc2626",background:"#fee2e2",padding:"2px 8px",borderRadius:12}}>🚨 URGENTE</span>}
              <span style={{fontSize:13,fontWeight:600,flex:1}}>{r.righe?.length} materiali richiesti</span>
              <span style={{fontSize:11,color:"#9ca3af"}}>da {r.richiestaFromName}</span>
              <span style={{fontSize:11,color:"#9ca3af"}}>{r.createdAt?.toDate?.()?.toLocaleDateString("it-IT")}</span>
              {isAperta ? <ChevronUp size={15} color="#9ca3af"/> : <ChevronDown size={15} color="#9ca3af"/>}
            </div>

            {isAperta && (
              <div style={{borderTop:"1px solid #f3f4f6",padding:"12px 16px"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,marginBottom:12}}>
                  <thead>
                    <tr style={{background:"#f9fafb"}}>
                      {["Descrizione","Codice","Qta","U.M.","Note"].map(h=>(
                        <th key={h} style={{padding:"6px 10px",textAlign:"left",fontSize:11,fontWeight:600,color:"#6b7280"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(r.righe||[]).map((riga,i)=>(
                      <tr key={i} style={{borderBottom:"1px solid #f3f4f6"}}>
                        <td style={{padding:"7px 10px",fontWeight:500}}>{riga.descrizione}</td>
                        <td onClick={()=>onNavigate&&onNavigate({page:"listino-materiali"})}
                        style={{padding:"7px 10px",color:"#7c3aed",fontFamily:"monospace",cursor:"pointer",textDecoration:"underline",textDecorationColor:"transparent",transition:"text-decoration-color 0.15s"}}
                        onMouseEnter={e=>e.currentTarget.style.textDecorationColor="#7c3aed"}
                        onMouseLeave={e=>e.currentTarget.style.textDecorationColor="transparent"}>
                        {riga.codice||"—"}
                      </td>
                        <td style={{padding:"7px 10px",fontWeight:700}}>{riga.quantita}</td>
                        <td style={{padding:"7px 10px",color:"#6b7280"}}>{riga.unitaMisura}</td>
                        <td style={{padding:"7px 10px",color:"#9ca3af"}}>{riga.note||"—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {r.noteGenerali && <div style={{fontSize:12,color:"#6b7280",background:"#f9fafb",padding:"8px 12px",borderRadius:6,marginBottom:8}}>📝 {r.noteGenerali}</div>}
                {r.noteEvasione && <div style={{fontSize:12,color:"#059669",background:"#d1fae5",padding:"8px 12px",borderRadius:6,marginBottom:8}}>✅ Magazzino: {r.noteEvasione}</div>}
                {r.stato==="chiusa" && r.chiusaDaNome && (
                  <div style={{fontSize:11,color:"#9ca3af",marginBottom:8}}>Chiusa da {r.chiusaDaNome} il {r.chiusaData?.toDate?.()?.toLocaleDateString("it-IT")}</div>
                )}
                {(r.stato==="in_attesa"||r.stato==="in_lavorazione") && (isOrgAdmin||isPlatformOwner) && (
                  <button onClick={()=>annulla(r.id)} style={{fontSize:12,color:"#dc2626",background:"none",border:"1px solid #fca5a5",borderRadius:6,padding:"4px 12px",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    Annulla richiesta
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {showModal && (
        <NuovaRichiestaModal projectId={projectId} projectName={projectName} orgId={orgId}
          currentUser={currentUser} onClose={()=>setShowModal(false)}/>
      )}
    </div>
  );
}
